import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useOptimistic,
  useState,
  useTransition,
  type ReactNode,
} from 'react'
import { listTasks, patchTask, patchTaskStatus } from '../api/tasks'
import { ApiError } from '../api/auth'
import type { EmailTask, TaskPriority, TaskStatus } from '../types'

type OptimisticUpdate = {
  id: string
  status?: TaskStatus
  priority?: TaskPriority | null
  owner_email?: string | null
  due_at?: string | null
  extra_instructions?: string | null
}

type TasksContextValue = {
  tasks: EmailTask[]
  optimistic: EmailTask[]
  loading: boolean
  pending: boolean
  error: string | null
  refresh: () => Promise<void>
  setStatus: (task: EmailTask, status: TaskStatus) => Promise<EmailTask | null>
  setPriority: (task: EmailTask, priority: TaskPriority | '') => void
  setOwner: (task: EmailTask, email: string) => void
  setDueAt: (task: EmailTask, isoDate: string) => void
  setExtraInstructions: (task: EmailTask, text: string) => void
  getTask: (id: string) => EmailTask | undefined
}

const TasksContext = createContext<TasksContextValue | null>(null)

function applyOptimisticUpdate(task: EmailTask, update: OptimisticUpdate): EmailTask {
  let next = task
  if (update.status !== undefined) next = { ...next, status: update.status }
  if (update.priority !== undefined) next = { ...next, priority: update.priority }
  if (update.owner_email !== undefined) next = { ...next, owner_email: update.owner_email }
  if (update.due_at !== undefined) next = { ...next, due_at: update.due_at }
  if (update.extra_instructions !== undefined) {
    next = {
      ...next,
      metadata: { ...next.metadata, extra_instructions: update.extra_instructions ?? undefined },
    }
    if (update.extra_instructions === null) {
      const { extra_instructions: _, ...rest } = next.metadata
      next = { ...next, metadata: rest }
    }
  }
  return next
}

export function TasksProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<EmailTask[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [pending, startTransition] = useTransition()
  const [optimistic, setOptimistic] = useOptimistic(
    tasks,
    (current, update: OptimisticUpdate) =>
      current.map((t) => (t.id === update.id ? applyOptimisticUpdate(t, update) : t)),
  )

  const refresh = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const rows = await listTasks({ status: 'all', limit: 200 })
      setTasks(rows)
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError(null)
        setTasks([])
      } else {
        const msg = e instanceof Error ? e.message : 'failed to load tasks'
        setError(msg === 'unauthorized' ? "Couldn't sign you in" : msg)
        setTasks([])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const applyUpdate = useCallback((updated: EmailTask) => {
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
  }, [])

  const setStatus = useCallback(
    (task: EmailTask, status: TaskStatus) => {
      return new Promise<EmailTask | null>((resolve) => {
        startTransition(async () => {
          setOptimistic({ id: task.id, status })
          try {
            const updated = await patchTaskStatus(task.id, status)
            applyUpdate(updated)
            resolve(updated)
          } catch (e) {
            setError(e instanceof Error ? e.message : 'update failed')
            void refresh()
            resolve(null)
          }
        })
      })
    },
    [applyUpdate, refresh, setOptimistic],
  )

  const setPriority = useCallback(
    (task: EmailTask, priority: TaskPriority | '') => {
      startTransition(async () => {
        const next = priority === '' ? null : priority
        setOptimistic({ id: task.id, priority: next })
        try {
          applyUpdate(
            await patchTask(
              task.id,
              next === null ? { clear_priority: true } : { priority: next },
            ),
          )
        } catch (e) {
          setError(e instanceof Error ? e.message : 'priority update failed')
          void refresh()
        }
      })
    },
    [applyUpdate, refresh, setOptimistic],
  )

  const setOwner = useCallback(
    (task: EmailTask, email: string) => {
      const trimmed = email.trim()
      const next = trimmed || null
      if (next === (task.owner_email ?? null)) return
      startTransition(async () => {
        setOptimistic({ id: task.id, owner_email: next })
        try {
          applyUpdate(
            await patchTask(
              task.id,
              next ? { owner_email: next } : { clear_owner: true },
            ),
          )
        } catch (e) {
          setError(e instanceof Error ? e.message : 'owner update failed')
          void refresh()
        }
      })
    },
    [applyUpdate, refresh, setOptimistic],
  )

  const setDueAt = useCallback(
    (task: EmailTask, isoDate: string) => {
      const next = isoDate.trim()
      if (!next) return
      const cur = task.due_at?.slice(0, 10) ?? null
      if (next === cur) return
      startTransition(async () => {
        setOptimistic({ id: task.id, due_at: next })
        try {
          applyUpdate(await patchTask(task.id, { due_at: next }))
        } catch (e) {
          setError(e instanceof Error ? e.message : 'due date update failed')
          void refresh()
        }
      })
    },
    [applyUpdate, refresh, setOptimistic],
  )

  const setExtraInstructions = useCallback(
    (task: EmailTask, text: string) => {
      const next = text.trim()
      const cur =
        typeof task.metadata?.extra_instructions === 'string'
          ? task.metadata.extra_instructions
          : ''
      if (next === cur) return
      startTransition(async () => {
        setOptimistic({ id: task.id, extra_instructions: next || null })
        try {
          applyUpdate(
            await patchTask(
              task.id,
              next
                ? { extra_instructions: next }
                : { clear_extra_instructions: true },
            ),
          )
        } catch (e) {
          setError(e instanceof Error ? e.message : 'instructions update failed')
          void refresh()
        }
      })
    },
    [applyUpdate, refresh, setOptimistic],
  )

  const getTask = useCallback(
    (id: string) => optimistic.find((t) => t.id === id),
    [optimistic],
  )

  const value = useMemo(
    () => ({
      tasks,
      optimistic,
      loading,
      pending,
      error,
      refresh,
      setStatus,
      setPriority,
      setOwner,
      setDueAt,
      setExtraInstructions,
      getTask,
    }),
    [
      tasks,
      optimistic,
      loading,
      pending,
      error,
      refresh,
      setStatus,
      setPriority,
      setOwner,
      setDueAt,
      setExtraInstructions,
      getTask,
    ],
  )

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
}

export function useTasks(): TasksContextValue {
  const ctx = useContext(TasksContext)
  if (!ctx) throw new Error('useTasks requires TasksProvider')
  return ctx
}

export function extraInstructions(task: EmailTask): string {
  const raw = task.metadata?.extra_instructions
  return typeof raw === 'string' ? raw : ''
}

export function dueDateInputValue(iso: string | null): string {
  if (!iso) return ''
  return iso.slice(0, 10)
}
